import { gql } from '@apollo/client';

// TODO: Remove mock-response assumptions in consumers once the Jira integration BE is live.

const JIRA_INTEGRATION_SETUP_FIELDS = gql`
  fragment JiraIntegrationSetupFields on JiraIntegrationSetup {
    mode
    currentStep
    instanceUrl
    appVersion
    selectedSiteId
    selectedSpaceIds
    error {
      title
      message
    }
    reconnectSummary {
      connectedSpacesCount
    }
    steps {
      step
      status
      title
      description
      detail
    }
    availableSites {
      id
      url
      selectable
    }
    availableSpaces {
      id
      name
      openTicketsCount
      selected
      avatarUrl
    }
    primaryAction {
      label
      actionType
      url
      loading
      disabled
    }
  }
`;

export const GET_JIRA_INTEGRATION_SETUP = gql`
  ${JIRA_INTEGRATION_SETUP_FIELDS}
  query jiraIntegrationSetup($args: JiraIntegrationSetupInput) {
    jiraIntegrationSetup(args: $args) {
      ...JiraIntegrationSetupFields
    }
  }
`;

export const QL_JIRA_INTEGRATION_SETUP_ACTION = gql`
  ${JIRA_INTEGRATION_SETUP_FIELDS}
  mutation jiraIntegrationSetupAction($args: JiraIntegrationSetupActionInput!) {
    jiraIntegrationSetupAction(args: $args) {
      ...JiraIntegrationSetupFields
    }
  }
`;

export const QL_JIRA_INTEGRATION_DISCONNECT = gql`
  mutation jiraIntegrationDisconnect {
    jiraIntegrationDisconnect {
      status
      instanceUrl
      spacesCount
      lastUpdated
    }
  }
`;

export const QL_JIRA_INTEGRATION_TEST_CONNECTION = gql`
  mutation jiraIntegrationTestConnection {
    jiraIntegrationTestConnection {
      success
      error
    }
  }
`;

export const GET_JIRA_INTEGRATION = gql`
  query jiraIntegration {
    jiraIntegration {
      status
      instanceUrl
      spacesCount
      lastUpdated
      oauthTokenActive
      apiConnectivityActive
      jiraInstallationId
      oauthInstallStepComplete
      jiraAppInstalledComplete
      connectionHealth {
        instanceUrl
        connectionStatusLabel
        metrics {
          connectedSpaces
          lastSync
        }
        systemChecks {
          id
          title
          description
          status
        }
        connectedSpaces {
          id
          name
          projectKey
          category
          openTicketsCount
          iconColorIndex
          avatarUrl
          lastUpdated
        }
        addableSpaces {
          id
          name
          openTicketsCount
          selected
          avatarUrl
        }
        statusMappingAutoSyncEnabled
      }
    }
  }
`;

// TODO: Replace mock persist once BE status-mapping auto-sync API is ready.
export const QL_JIRA_INTEGRATION_SET_STATUS_MAPPING_AUTO_SYNC = gql`
  mutation jiraIntegrationSetStatusMappingAutoSync($args: JiraIntegrationSetStatusMappingAutoSyncInput!) {
    jiraIntegrationSetStatusMappingAutoSync(args: $args) {
      success
      error
    }
  }
`;

export const QL_JIRA_INTEGRATION_RECONNECT = gql`
  mutation jiraIntegrationReconnect {
    jiraIntegrationReconnect {
      status
      instanceUrl
      spacesCount
      lastUpdated
      oauthTokenActive
      apiConnectivityActive
      jiraInstallationId
      oauthInstallStepComplete
      jiraAppInstalledComplete
    }
  }
`;

export const QL_JIRA_INTEGRATION_DELETE_CONNECTED_SPACE = gql`
  mutation jiraIntegrationDeleteConnectedSpace($args: JiraIntegrationDeleteConnectedSpaceInput!) {
    jiraIntegrationDeleteConnectedSpace(args: $args) {
      success
      error
    }
  }
`;

export const QL_JIRA_INTEGRATION_ADD_CONNECTED_SPACE = gql`
  mutation jiraIntegrationAddConnectedSpace($args: JiraIntegrationAddConnectedSpaceInput!) {
    jiraIntegrationAddConnectedSpace(args: $args) {
      success
      error
    }
  }
`;

export const GET_VULNERABILITY_JIRA_TICKETS = gql`
  query vulnerabilityJiraTickets($args: VulnerabilityJiraTicketsInput!) {
    vulnerabilityJiraTickets(args: $args) {
      id
      ticketKey
      projectId
      spaceName
      status
      statusCategory
      statusCategoryColor
      assignee
      assigneeAvatarUrl
      issueType
      issueTypeIconUrl
      updatedAt
      url
    }
  }
`;

export const GET_JIRA_SPACE_ISSUE_TYPES = gql`
  query jiraSpaceIssueTypes($args: JiraSpaceIssueTypesInput!) {
    jiraSpaceIssueTypes(args: $args) {
      id
      name
      iconUrl
      subtask
    }
  }
`;

export const GET_JIRA_SPACE_WORKFLOW_CONFIG = gql`
  query jiraSpaceWorkflowConfig($args: JiraSpaceWorkflowConfigInput!) {
    jiraSpaceWorkflowConfig(args: $args) {
      spaceId
      issueTypeId
      jiraStatuses {
        id
        name
        category
      }
      netriseStatuses
      mappings {
        jiraStatusId
        netriseStatusId
      }
      resolutionStatusIds
    }
  }
`;

export const GET_JIRA_STATUS_MAPPING_PROBLEMS = gql`
  query jiraStatusMappingProblems($args: JiraStatusMappingProblemsInput) {
    jiraStatusMappingProblems(args: $args) {
      jiraSpaceId
      projectKey
      projectName
      spaceProblems {
        code
        detail
        detectedAt
      }
      mappingProblems {
        jiraStatusMappingId
        jiraIssueTypeId
        jiraStatusId
        jiraStatusName
        code
        detail
        detectedAt
      }
    }
  }
`;

export const QL_SAVE_JIRA_SPACE_WORKFLOW_CONFIG = gql`
  mutation saveJiraSpaceWorkflowConfig($args: SaveJiraSpaceWorkflowConfigInput!) {
    saveJiraSpaceWorkflowConfig(args: $args) {
      success
      error
    }
  }
`;

export const GET_JIRA_SPACE_ISSUE_FIELDS = gql`
  query jiraSpaceIssueFields($args: JiraSpaceIssueFieldsInput!) {
    jiraSpaceIssueFields(args: $args) {
      key
      name
      required
      schemaType
      schemaCustom
      itemType
      supported
      unsupportedRequired
      allowedValues {
        id
        value
      }
    }
  }
`;

export const GET_JIRA_PROJECT_USERS = gql`
  query jiraProjectUsers($args: JiraProjectUsersInput!) {
    jiraProjectUsers(args: $args) {
      accountId
      displayName
      emailAddress
      avatarUrl
      active
    }
  }
`;

export const GET_JIRA_PROJECT_VERSIONS = gql`
  query jiraProjectVersions($args: JiraProjectVersionsInput!) {
    jiraProjectVersions(args: $args) {
      id
      name
      description
      released
      archived
      releaseDate
    }
  }
`;

export const GET_JIRA_PROJECT_COMPONENTS = gql`
  query jiraProjectComponents($args: JiraProjectComponentsInput!) {
    jiraProjectComponents(args: $args) {
      id
      name
      description
    }
  }
`;

export const GET_JIRA_PROJECT_LABELS = gql`
  query jiraProjectLabels($args: JiraProjectLabelsInput!) {
    jiraProjectLabels(args: $args) {
      name
    }
  }
`;

export const GET_JIRA_PROJECT_SPRINTS = gql`
  query jiraProjectSprints($args: JiraProjectSprintsInput!) {
    jiraProjectSprints(args: $args) {
      id
      name
      state
      boardId
      startDate
      endDate
    }
  }
`;

export const GET_JIRA_PROJECT_TEAMS = gql`
  query jiraProjectTeams($args: JiraProjectTeamsInput!) {
    jiraProjectTeams(args: $args) {
      id
      displayName
      avatarUrl
    }
  }
`;

export const QL_JIRA_INTEGRATION_CREATE_ISSUE = gql`
  mutation jiraIntegrationCreateIssue($args: JiraIntegrationCreateIssueInput!) {
    jiraIntegrationCreateIssue(args: $args) {
      success
      error
      ticket {
        id
        ticketKey
        projectId
        spaceName
        status
        assignee
        updatedAt
        url
      }
    }
  }
`;
